import React, { useState, useEffect } from 'react';
import ProcessMap, {
  IProcessMapNodeDetailsViewModel
} from './ProcessMap/ProcessMap';
import ProcessDetails from './ProcessDetails/ProcessDetails';
import Feedback from './Feedback/Feedback';
import Header from './Header/Header';
import { useService } from '@Client/runner.hooks/useService';
import CustomTabs, {
  ICustomTab
} from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import { SharedAngular } from '@Client/@types/sharedAngular';
import IProcessMap from '@Shared.Angular/@types/processMap';
import CommentTargetType from '@Shared.Angular/@types/core/contracts/constants/commentTargetType';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { ProcessMapRunnerViewTabs } from '@Shared.Angular/flowingly.services/flowingly.constants';
import { Guid } from '@Shared.Angular/@types/guid';
import CustomTabPanel from '@Shared.Angular/flowingly.components/CustomTabs/CustomTab/CustomTabPanel';

type Props = {
  processMapId: Guid;
  processMapHeight: number;
  onProcessMapRetrieved: (processMap: IProcessMap) => void;
  onSelectedNodeDataChange: (data: IProcessMapNodeDetailsViewModel) => void;
  hideFeedBack: boolean;
  isPublicMapView: boolean;
};

const LeftPanel = (props: Props) => {
  const {
    processMapId,
    processMapHeight,
    onSelectedNodeDataChange,
    onProcessMapRetrieved,
    hideFeedBack,
    isPublicMapView
  } = props;
  const [selectedTab, setSelectedTab] = useState(0);
  const [processMap, setProcessMap] = useState<IProcessMap>({} as IProcessMap);
  const [commentTargetType, setCommentTargetType] = useState(null);
  const [commentLength, setCommentLength] = useState(0);
  const [editableByCurrentUser, setEditableByCurrentUser] = useState(false);
  const appConfig = useService<SharedAngular.APP_CONFIG>('APP_CONFIG');

  useEffect(() => {
    // set ProcessMap, the default tab when switching between components using breadcrumbs
    handleChange(null, 0);
  }, [processMapId]);

  const shouldFeedbackBeVisible = (editableByCurrentUser: boolean) => {
    return (
      appConfig.flowModelFeedbackVisibility.toLowerCase() === 'all' ||
      (appConfig.flowModelFeedbackVisibility.toLowerCase() === 'flowowners' &&
        editableByCurrentUser)
    );
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: React.SetStateAction<number>
  ) => {
    setSelectedTab(newValue);
    onSelectedNodeDataChange(null);
  };

  const handleProcessMapLoad = (data) => {
    setProcessMap(data);
    if (shouldFeedbackBeVisible(data.editableByCurrentUser)) {
      setCommentTargetType(CommentTargetType.FLOW_MODEL);
    }
    setEditableByCurrentUser(data.editableByCurrentUser);
  };

  const handleCommentsUpdate = (totalComments: number) => {
    setCommentLength(totalComments);
  };

  const tabs: ICustomTab[] = [
    { key: ProcessMapRunnerViewTabs.PROCESS_MAP, label: 'Process Map' },
    { key: ProcessMapRunnerViewTabs.PROCESS_DETAILS, label: 'Process Details' },
    {
      key: ProcessMapRunnerViewTabs.FLOW_MODEL_TAGS,
      label: 'Flow Model Tags',
      isHidden: !appConfig.enableTagging
    },
    {
      key: ProcessMapRunnerViewTabs.FEEDBACK,
      label: `${
        commentLength > 0 ? `Feedback (${commentLength})` : 'Feedback'
      }`,
      isDisabled: !shouldFeedbackBeVisible(editableByCurrentUser),
      isHidden: hideFeedBack
    }
  ];

  const visibleTabs = tabs.filter((tab) => !tab.isHidden);

  const tabIndices = visibleTabs.reduce((acc, tab, index) => {
    acc[tab.key] = index;
    return acc;
  }, {} as Record<string, number>);

  const tagGroupsFormatted = (tagGroups: ITagGroupDetail[]) => {
    const result: Record<string, { tags: string[]; color: string }> = {};
    tagGroups.forEach((group) => {
      result[group.name] = {
        tags: group.tags.map((tag) => tag.name),
        color: group.color
      };
    });
    return result;
  };

  return (
    <div id="process-map-leftpanel">
      <Header
        processOwner={processMap.processOwner}
        processDescription={processMap.description}
      />
      <CustomTabs
        selectedTabIndex={selectedTab}
        onChange={handleChange}
        tabs={visibleTabs}
      />
      <CustomTabPanel
        value={selectedTab}
        index={tabIndices[ProcessMapRunnerViewTabs.PROCESS_MAP]}
      >
        <ProcessMap
          isPublicMapView={isPublicMapView}
          processMapHeight={processMapHeight}
          isVisible={
            selectedTab === tabIndices[ProcessMapRunnerViewTabs.PROCESS_MAP]
          }
          processMapId={processMapId}
          onProcessMapRetrieved={onProcessMapRetrieved}
          onSelectedNodeDataChange={onSelectedNodeDataChange}
          onProcessMapLoad={handleProcessMapLoad}
        />
      </CustomTabPanel>
      <CustomTabPanel
        value={selectedTab}
        index={tabIndices[ProcessMapRunnerViewTabs.PROCESS_DETAILS]}
      >
        <ProcessDetails isPublicMapView={isPublicMapView} {...processMap} />
      </CustomTabPanel>

      <CustomTabPanel
        value={selectedTab}
        index={tabIndices[ProcessMapRunnerViewTabs.FLOW_MODEL_TAGS]}
      >
        <div className="process-map-tags-container">
          <TagList
            showBorder={false}
            groupedSelectedTags={tagGroupsFormatted(processMap.tags || [])}
          />
        </div>
      </CustomTabPanel>

      <CustomTabPanel
        value={selectedTab}
        index={tabIndices[ProcessMapRunnerViewTabs.FEEDBACK]}
      >
        <Feedback
          commentAdded
          commentTargetId={processMapId}
          commentTargetType={commentTargetType}
          editableByCurrentUser={editableByCurrentUser}
          onCommentsUpdate={handleCommentsUpdate}
        ></Feedback>
      </CustomTabPanel>
    </div>
  );
};

export default LeftPanel;
